import axios from '../utils/axios';
import { getLocalData, saveLocalData } from './localDatabase';
import { mergeOfflineData } from './offlineDataMerger';

/**
 * Sync all master data from server to local IndexedDB
 * Called on login to populate local cache
 */
export const syncAllMasterData = async () => {
    const collections = [
        { name: 'brands', endpoint: '/brands', dataKey: 'brands' },
        { name: 'categories', endpoint: '/categories', dataKey: 'categories' },
        { name: 'products', endpoint: '/products', dataKey: 'products', params: { status: 'active' } },
        { name: 'suppliers', endpoint: '/suppliers', dataKey: 'suppliers' },
        { name: 'customers', endpoint: '/customers', dataKey: 'customers', params: { isActive: true, forInvoicing: true } },
        // AR lists for field offline (best-effort — skip failures quietly)
        { name: 'invoices', endpoint: '/customer-invoices', dataKey: 'invoices', params: { limit: 100 } },
        { name: 'receipts', endpoint: '/customer-payments', dataKey: 'payments', params: { limit: 100 } },
    ];

    const results = {
        success: [],
        failed: [],
    };

    for (const { name, endpoint, dataKey, params } of collections) {
        try {
            const response = await axios.get(endpoint, params ? { params } : undefined);
            const serverData = response.data.data?.[dataKey]
                || response.data.data?.[name]
                || (Array.isArray(response.data.data) ? response.data.data : []);

            if (!Array.isArray(serverData)) {
                results.failed.push(name);
                continue;
            }

            // Merge with offline pending records
            const offlineCollectionName = name.startsWith('master_') ? name : `master_${name}`;
            const pendingCollection = name === 'receipts' ? 'receipts' : offlineCollectionName;
            const merged = await mergeOfflineData(
                serverData,
                name === 'invoices' ? 'invoices' : pendingCollection,
                (r) => (r.payload ? { _id: r.localId, ...r.payload, ...r, isOffline: true } : r)
            );

            await saveLocalData(name, merged);

            results.success.push(name);
            console.log(`✅ Synced ${name}: ${merged.length} records`);
        } catch (err) {
            results.failed.push(name);
            console.warn(`❌ Failed to sync ${name}:`, err.message);
        }
    }

    return results;
};

/**
 * Sync a single collection from server to local IndexedDB
 * @param {string} collection - Collection name (brands, products, etc.)
 * @param {string} endpoint - API endpoint
 * @param {string} dataKey - Key in response.data.data
 */
export const syncCollection = async (collection, endpoint, dataKey) => {
    try {
        const response = await axios.get(endpoint);
        const serverData = response.data.data[dataKey];

        // Merge with offline pending records
        // Use master_ prefix for offline storage collection names
        const offlineCollectionName = collection.startsWith('master_') ? collection : `master_${collection}`;
        const merged = await mergeOfflineData(serverData, offlineCollectionName, (r) => r.payload);

        // Save to master collection
        await saveLocalData(collection, merged);

        console.log(`✅ Synced ${collection}: ${merged.length} records`);
        return { success: true, count: merged.length };
    } catch (err) {
        console.warn(`❌ Failed to sync ${collection}:`, err.message);
        return { success: false, error: err.message };
    }
};

/**
 * Start periodic background sync
 * Syncs all master data at regular intervals when online
 * @param {number} intervalMinutes - Sync interval in minutes (default: 5)
 * @returns {number} Interval ID (use clearInterval to stop)
 */
export const startBackgroundSync = (intervalMinutes = 15) => {
    const intervalId = setInterval(async () => {
        if (!navigator.onLine) return;
        // Skip when tab is hidden to reduce background API load
        if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
        console.log('🔄 Running background sync...');
        await syncAllMasterData();
    }, intervalMinutes * 60 * 1000);

    console.log(`🚀 Background sync started (every ${intervalMinutes} minutes)`);
    return intervalId;
};

/**
 * Stop background sync
 * @param {number} intervalId - Interval ID from startBackgroundSync
 */
export const stopBackgroundSync = (intervalId) => {
    if (intervalId) {
        clearInterval(intervalId);
        console.log('⏹️ Background sync stopped');
    }
};
